import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, UserPlus, UserRoundPlus, Eye, Search, Phone, List, LayoutGrid } from "lucide-react";
import AppShell from "../../shell/AppShell.jsx";
import DetailDrawer from "../../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../../hierarchy/BackofficeIdentityContext.jsx";
import { useAgentPortalGuard } from "../useAgentPortalGuard.js";
import AgentPortalGuardState from "../AgentPortalGuardState.jsx";
import { useAgentPersonRoster } from "./useAgentPersonRoster.js";
import { useBuddyActiveCounts } from "./useBuddyActiveCounts.js";
import { useSourcePersonBuddies } from "./useSourcePersonBuddies.js";
import { useBuddyNetworkSummary } from "./useBuddyNetworkSummary.js";
import { BuddyContactForm, PersonDetailView } from "./BuddyGuaranteeDrawerContent.jsx";
import { BuddyNetworkChart, BuddyDistributionChart } from "./BuddyNetworkCharts.jsx";
import {
  describeSourcePipelineStatus,
  sourceKey,
  formatDateTh,
  MAX_ACTIVE_BUDDIES,
  ROSTER_FILTERS,
  resolveRosterSourceMode,
  matchesRosterCountFilter,
  computeRosterKpis,
  buildDistributionBuckets,
} from "./buddyGuaranteeShared.js";
import "./buddyGuarantees.css";
import "../../pages/states.css";
import Pagination from "../../components/Pagination.jsx";

import PageSkeleton from "../../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Agent", "Buddy Guarantee"];
const DRAWER_TITLE_ID = "bg-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;
// A practical enterprise default, not the backend's own max page size -
// requirement: "Do not hardcode pageSize 100 as the whole truth." Real
// server-side page/search keeps this usable no matter how many thousands
// of real people exist in the Agent's scope.
const PAGE_SIZE = 20;
// The "ทั้งหมด"/count-based filters merge two real, independently-paginated
// backend sources for display - each is fetched at half PAGE_SIZE so a
// merged page never exceeds PAGE_SIZE items, matching every other filter's
// own page size.
const MERGED_PAGE_SIZE = Math.ceil(PAGE_SIZE / 2);

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "ไม่สามารถโหลดรายชื่อได้ กรุณาลองใหม่อีกครั้ง";
}

function combineStatus(a, b) {
  if (a === "error" || b === "error") return "error";
  if (a === "loading" || b === "loading") return "loading";
  if (a === "idle" || b === "idle") return "idle";
  return "ready";
}

function formatCount(value) {
  return typeof value === "number" ? value.toLocaleString("th-TH") : "-";
}

/**
 * /backoffice/agent/buddy-guarantees - Prompt 140C's worker-to-worker
 * network rebuild. Buddy Guarantee is not a free-text contact form: a
 * Buddy is another real worker/applicant in the same Agent line. Quick
 * Registered applicants and Worker Profiles are both real, immediately-
 * selectable candidates - e-KYC/Worker Profile/Ready for Match are
 * onboarding STATUSES, never existence gates. This page shows a real KPI
 * strip, a network chart (center person + up to 5 real buddy nodes), a
 * real 0/5..5/5 coverage distribution chart, and a large-scale roster
 * (server-side page/search, six real filters, list/grid toggle).
 */
export default function BuddyGuaranteesPage() {
  const guard = useAgentPortalGuard();
  const { data: identity } = useBackofficeIdentity();
  const enabled = guard.status === "ready";

  const [filter, setFilter] = useState("All");
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [drawer, setDrawer] = useState(null); // null | { mode: "person-detail", source } | { mode: "create", source: candidate|null }
  const [previewSource, setPreviewSource] = useState(null);

  // Debounced search commit - identical timing to every other Agent Portal
  // search field in this codebase (350ms).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  function handleFilterChange(nextFilter) {
    setFilter(nextFilter);
    setPage(1);
  }

  const sourceMode = resolveRosterSourceMode(filter);
  const isMerged = sourceMode === "All";

  // Both real rosters are always fetched (the inactive one at pageSize:1
  // purely for its own real totalCount, used for the KPI strip's "total
  // people in line" and the tab-count labels) - see P140B-R2's own
  // precedent this pattern is unchanged from.
  const applicationRoster = useAgentPersonRoster("WorkerApplication", {
    enabled,
    page: sourceMode === "WorkerProfile" ? 1 : page,
    pageSize: sourceMode === "WorkerProfile" ? 1 : isMerged ? MERGED_PAGE_SIZE : PAGE_SIZE,
    search: sourceMode === "WorkerProfile" ? "" : search,
  });
  const profileRoster = useAgentPersonRoster("WorkerProfile", {
    enabled,
    page: sourceMode === "WorkerApplication" ? 1 : page,
    pageSize: sourceMode === "WorkerApplication" ? 1 : isMerged ? MERGED_PAGE_SIZE : PAGE_SIZE,
    search: sourceMode === "WorkerApplication" ? "" : search,
  });

  // Memoized on the two roster hooks' own real items arrays (each stable -
  // only replaced when their own fetch actually resolves) - never
  // recomputed on every render, which would otherwise hand
  // useBuddyActiveCounts below a brand-new array reference each render and
  // re-trigger its own fetch effect in an infinite loop.
  const candidates = useMemo(() => {
    if (sourceMode === "WorkerApplication") {
      return applicationRoster.items;
    }
    if (sourceMode === "WorkerProfile") {
      return profileRoster.items;
    }
    return [...applicationRoster.items, ...profileRoster.items].sort(
      (a, b) => new Date(b.createdAtUtc ?? 0).getTime() - new Date(a.createdAtUtc ?? 0).getTime()
    );
  }, [sourceMode, applicationRoster.items, profileRoster.items]);

  const rosterStatus =
    sourceMode === "WorkerApplication" ? applicationRoster.status : sourceMode === "WorkerProfile" ? profileRoster.status : combineStatus(applicationRoster.status, profileRoster.status);
  const rosterError = sourceMode === "WorkerApplication" ? applicationRoster.error : sourceMode === "WorkerProfile" ? profileRoster.error : (applicationRoster.error ?? profileRoster.error);

  const buddyCounts = useBuddyActiveCounts(candidates);
  const networkSummary = useBuddyNetworkSummary(enabled);
  const previewBuddies = useSourcePersonBuddies(previewSource, { buddyStatus: "Active" });

  const filteringByCount = filter === "NoBuddy" || filter === "HasBuddy" || filter === "Full";
  const buddyCountsReady = buddyCounts.status === "ready";
  const displayedCandidates = filteringByCount
    ? candidates.filter((candidate) => matchesRosterCountFilter(filter, buddyCounts.counts[sourceKey(candidate)]))
    : candidates;

  if (guard.status !== "ready") {
    return <AgentPortalGuardState crumbs={CRUMBS} guard={guard} />;
  }

  function closeDrawer() {
    setDrawer(null);
  }

  function handleOpenDetail(candidate) {
    setDrawer({ mode: "person-detail", source: candidate });
    setPreviewSource(candidate);
  }

  function handleBuddyCountChanged(source, newCount) {
    buddyCounts.setCount(source, typeof newCount === "number" ? newCount : undefined);
    networkSummary.refetch();
    if (previewSource && sourceKey(previewSource) === sourceKey(source)) {
      previewBuddies.refetch();
    }
  }

  function refetchRosters() {
    applicationRoster.refetch();
    profileRoster.refetch();
  }

  const totalPeopleInLine = (applicationRoster.totalCount ?? 0) + (profileRoster.totalCount ?? 0);
  const kpis = computeRosterKpis({ totalPeopleInLine, networkSummary: networkSummary.summary });
  const distributionBuckets = buildDistributionBuckets({ totalPeopleInLine, networkSummary: networkSummary.summary });

  const activeTotalCount = sourceMode === "WorkerApplication" ? applicationRoster.totalCount : sourceMode === "WorkerProfile" ? profileRoster.totalCount : totalPeopleInLine;
  const activePageSize = isMerged ? MERGED_PAGE_SIZE : PAGE_SIZE;
  const totalPages = isMerged
    ? Math.max(Math.ceil((applicationRoster.totalCount ?? 0) / MERGED_PAGE_SIZE) || 1, Math.ceil((profileRoster.totalCount ?? 0) / MERGED_PAGE_SIZE) || 1)
    : activeTotalCount
      ? Math.max(1, Math.ceil(activeTotalCount / activePageSize))
      : 1;

  // The merged "ทั้งหมด" view combines two independently-paginated real
  // sources, so a precise "แสดง N-M จาก Total" range would misrepresent
  // exact position - an honest total-in-scope count is shown instead.
  const rangeCaption = isMerged
    ? activeTotalCount
      ? `ทั้งหมด ${activeTotalCount.toLocaleString("th-TH")} คนในสายงาน`
      : null
    : activeTotalCount
      ? `แสดง ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, activeTotalCount)} จาก ${activeTotalCount.toLocaleString("th-TH")}`
      : null;

  const activeFilterLabel = ROSTER_FILTERS.find((f) => f.value === filter)?.label ?? filter;

  const bothRostersEmpty =
    !search &&
    applicationRoster.status === "ready" &&
    profileRoster.status === "ready" &&
    (applicationRoster.totalCount ?? 0) === 0 &&
    (profileRoster.totalCount ?? 0) === 0;

  const emptyResultsMessage = search
    ? "ไม่พบรายการที่ตรงกับคำค้นหา"
    : filteringByCount
      ? "ไม่พบคนที่ตรงกับตัวกรองนี้ในหน้านี้"
      : `ยังไม่มีคนในสถานะ "${activeFilterLabel}" ในสายงานนี้`;

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-bg-page">
        <header className="wx-bg-head">
          <div>
            <h1>Buddy Guarantee</h1>
            {identity?.primaryRole && (
              <span className="wx-bg-scope-pill">
                Scope <b>{identity.primaryRole}</b>
              </span>
            )}
          </div>
          <button
            type="button"
            className="wx-button wx-button--primary wx-bg-add-buddy-trigger"
            onClick={() => setDrawer({ mode: "create", source: null })}
          >
            <UserPlus size={15} aria-hidden="true" />
            เพิ่ม Buddy
          </button>
        </header>

        <section className="wx-bg-explainer">
          <ShieldCheck size={18} aria-hidden="true" className="wx-bg-explainer-icon" />
          <ul>
            <li>Buddy คือแรงงานหรือผู้สมัครอีกคนหนึ่งในสายงานเดียวกัน - ไม่ใช่ผู้ติดต่อภายนอกที่พิมพ์ชื่อ/เบอร์โทรเอง</li>
            <li>ไม่ใช่ live tracking ครอบครัว</li>
            <li>ไม่ใช่ระบบแจ้งเตือนจริง</li>
            <li>SOS buddy fan-out สูงสุด 5 คน ตาม policy</li>
          </ul>
        </section>

        {bothRostersEmpty ? (
          <section className="wx-panel wx-bg-list-panel">
            <p className="wx-empty-list">ยังไม่มีแรงงานหรือผู้สมัครในสายงานของคุณ</p>
          </section>
        ) : (
          <>
            <section className="wx-bg-kpi-strip" aria-label="ภาพรวม Buddy Guarantee">
              <div className="wx-bg-kpi-tile">
                <span className="wx-bg-kpi-value">{formatCount(kpis.totalPeopleInLine)}</span>
                <span className="wx-bg-kpi-label">คนในสายงานทั้งหมด</span>
              </div>
              <div className="wx-bg-kpi-tile">
                <span className="wx-bg-kpi-value">{formatCount(kpis.peopleWithBuddy)}</span>
                <span className="wx-bg-kpi-label">มี Buddy แล้ว</span>
              </div>
              <div className="wx-bg-kpi-tile">
                <span className="wx-bg-kpi-value">{formatCount(kpis.noBuddy)}</span>
                <span className="wx-bg-kpi-label">ยังไม่มี Buddy</span>
              </div>
              <div className="wx-bg-kpi-tile">
                <span className="wx-bg-kpi-value">{kpis.averagePerPerson.toFixed(1)}</span>
                <span className="wx-bg-kpi-label">Buddy เฉลี่ยต่อคน</span>
              </div>
              <div className="wx-bg-kpi-tile">
                <span className="wx-bg-kpi-value">{formatCount(kpis.atOrNearCap)}</span>
                <span className="wx-bg-kpi-label">เกิน/ใกล้เต็ม 5 คน</span>
              </div>
            </section>

            <section className="wx-bg-charts-grid">
              <div className="wx-panel wx-bg-network-panel">
                <h2 className="wx-bg-section-title">เครือข่าย Buddy</h2>
                {previewSource ? (
                  <>
                    <p className="wx-bg-section-desc">
                      เครือข่าย Buddy ของ <strong>{previewSource.name}</strong>
                    </p>
                    <BuddyNetworkChart
                      centerPerson={previewSource}
                      buddies={previewBuddies.items
                        .filter((b) => b.buddyPerson)
                        .map((b) => ({ name: b.buddyPerson.name, sourceType: b.buddyPerson.personType, pipelineStatus: b.buddyPerson.pipelineStatus }))}
                    />
                  </>
                ) : (
                  <p className="wx-empty-list">เลือกคนในสายงานด้านล่างเพื่อดูเครือข่าย Buddy ของเขา</p>
                )}
              </div>
              <div className="wx-panel wx-bg-dist-panel">
                <h2 className="wx-bg-section-title">การกระจายจำนวน Buddy ต่อคน</h2>
                <p className="wx-bg-section-desc">จำนวนคนในสายงานแบ่งตามจำนวน Buddy ที่มี (0 ถึง 5)</p>
                <BuddyDistributionChart buckets={distributionBuckets} />
              </div>
            </section>

            <section className="wx-panel wx-bg-list-panel">
              <div className="wx-bg-panel-head">
                <h2 className="wx-bg-section-title">แรงงานในสายงาน</h2>
                <div className="wx-bg-view-toggle" role="group" aria-label="รูปแบบการแสดงผล">
                  <button
                    type="button"
                    className={viewMode === "list" ? "wx-bg-view-toggle-button wx-bg-view-toggle-button--active" : "wx-bg-view-toggle-button"}
                    aria-pressed={viewMode === "list"}
                    aria-label="แสดงแบบรายการ"
                    title="แสดงแบบรายการ"
                    onClick={() => setViewMode("list")}
                  >
                    <List size={15} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={viewMode === "grid" ? "wx-bg-view-toggle-button wx-bg-view-toggle-button--active" : "wx-bg-view-toggle-button"}
                    aria-pressed={viewMode === "grid"}
                    aria-label="แสดงแบบกริด"
                    title="แสดงแบบกริด"
                    onClick={() => setViewMode("grid")}
                  >
                    <LayoutGrid size={15} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="wx-bg-tabs" role="tablist" aria-label="ตัวกรองแรงงานในสายงาน">
                {ROSTER_FILTERS.map((filterDef) => (
                  <button
                    key={filterDef.value}
                    type="button"
                    role="tab"
                    aria-selected={filter === filterDef.value}
                    className={filter === filterDef.value ? "wx-bg-tab wx-bg-tab--active" : "wx-bg-tab"}
                    onClick={() => handleFilterChange(filterDef.value)}
                  >
                    {filterDef.label}
                  </button>
                ))}
              </div>

              <div className="wx-bg-toolbar">
                <label className="wx-bg-search">
                  <Search size={14} aria-hidden="true" className="wx-bg-search-icon" />
                  <span className="wx-visually-hidden">ค้นหาชื่อแรงงาน ผู้สมัคร เบอร์โทร รหัส Invite หรือหนังสือเดินทาง</span>
                  <input
                    type="search"
                    placeholder="ค้นหาชื่อ เบอร์โทร รหัส Invite หรือหนังสือเดินทาง..."
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                </label>
              </div>

              {rosterStatus === "loading" && candidates.length === 0 ? (
                <PageSkeleton variant="table" label="กำลังโหลดรายชื่อ..." />
              ) : rosterStatus === "error" ? (
                <div className="wx-state-card wx-state-card--danger" role="alert">
                  <p className="wx-state-body">{describeListError(rosterError)}</p>
                </div>
              ) : filteringByCount && !buddyCountsReady ? (
                <PageSkeleton variant="table" label="กำลังตรวจสอบจำนวน Buddy..." />
              ) : displayedCandidates.length === 0 ? (
                <p className="wx-empty-list">{emptyResultsMessage}</p>
              ) : (
                <>
                  {rangeCaption && <p className="wx-bg-worker-range">{rangeCaption}</p>}

                  {viewMode === "list" ? (
                    <div className="wx-bg-table-wrap">
                      <table className="wx-bg-table">
                        <thead>
                          <tr>
                            <th>บุคคล</th>
                            <th>สัญชาติ/ทักษะ</th>
                            <th>สถานะสายงาน</th>
                            <th>Buddy</th>
                            <th>Agent เจ้าของสายงาน</th>
                            <th>เข้าสายงานเมื่อ</th>
                            <th aria-hidden="true" />
                          </tr>
                        </thead>
                        <tbody>
                          {displayedCandidates.map((candidate) => (
                            <PersonRow
                              key={sourceKey(candidate)}
                              candidate={candidate}
                              activeCount={buddyCounts.counts[sourceKey(candidate)]}
                              onOpenDetail={() => handleOpenDetail(candidate)}
                              onQuickAdd={() => setDrawer({ mode: "create", source: candidate })}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="wx-bg-grid">
                      {displayedCandidates.map((candidate) => (
                        <PersonCard
                          key={sourceKey(candidate)}
                          candidate={candidate}
                          activeCount={buddyCounts.counts[sourceKey(candidate)]}
                          onOpenDetail={() => handleOpenDetail(candidate)}
                          onQuickAdd={() => setDrawer({ mode: "create", source: candidate })}
                        />
                      ))}
                    </div>
                  )}

                  {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
                  )}
                </>
              )}
            </section>
          </>
        )}
      </div>

      <DetailDrawer
        open={drawer !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector=".wx-bg-row, .wx-bg-card, .wx-bg-add-buddy-trigger, .wx-bg-quick-add"
      >
        {drawer?.mode === "person-detail" && (
          <PersonDetailView source={drawer.source} onBuddyCountChanged={handleBuddyCountChanged} onClose={closeDrawer} titleId={DRAWER_TITLE_ID} />
        )}
        {drawer?.mode === "create" && (
          <BuddyContactForm
            source={drawer.source}
            initialSourceType={sourceMode === "WorkerProfile" ? "WorkerProfile" : "WorkerApplication"}
            onSuccess={(detail, source) => {
              handleBuddyCountChanged(source, detail?.summary?.activeCount ?? null);
              if (!drawer.source) {
                // Picked via the global picker - that person might not be
                // on the currently visible roster page, so refresh it for
                // correctness. A row's own quick-add never needs this (the
                // person is already the row being looked at).
                refetchRosters();
              }
              closeDrawer();
            }}
            onClose={closeDrawer}
            titleId={DRAWER_TITLE_ID}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function PersonRow({ candidate, activeCount, onOpenDetail, onQuickAdd }) {
  const pipeline = describeSourcePipelineStatus(candidate.sourceType, candidate.pipelineStatus);
  const countKnown = typeof activeCount === "number";
  const atCap = countKnown && activeCount >= MAX_ACTIVE_BUDDIES;

  return (
    <tr className="wx-bg-row" onClick={onOpenDetail} data-label={candidate.name}>
      <td data-label="บุคคล">
        <span className="wx-bg-cell-strong">{candidate.name}</span>
        {candidate.phoneNumber && (
          <span className="wx-bg-cell-sub wx-bg-cell-sub--icon">
            <Phone size={10} aria-hidden="true" /> {candidate.phoneNumber}
          </span>
        )}
      </td>
      <td data-label="สัญชาติ/ทักษะ">
        <span className="wx-bg-cell-strong">{candidate.nationality ?? "-"}</span>
        {candidate.skillName && <span className="wx-bg-cell-sub">{candidate.skillName}</span>}
      </td>
      <td data-label="สถานะสายงาน">
        <span className={`wx-badge wx-badge--${pipeline.badgeVariant}`}>{pipeline.label}</span>
      </td>
      <td data-label="Buddy">
        <span className="wx-bg-cell-strong">{countKnown ? `${activeCount} / ${MAX_ACTIVE_BUDDIES}` : "..."}</span>
      </td>
      <td data-label="Agent เจ้าของสายงาน">
        <span className="wx-bg-cell-strong">{candidate.agentName ?? "-"}</span>
        {candidate.agentCode && <span className="wx-bg-cell-sub">{candidate.agentCode}</span>}
      </td>
      <td data-label="เข้าสายงานเมื่อ">{formatDateTh(candidate.createdAtUtc) ?? "-"}</td>
      <td data-label="" className="wx-bg-row-actions-cell">
        <button
          type="button"
          className="wx-bg-icon-button wx-bg-quick-add"
          title="เพิ่ม Buddy"
          aria-label={`เพิ่ม Buddy สำหรับ ${candidate.name}`}
          onClick={(event) => {
            event.stopPropagation();
            onQuickAdd();
          }}
          disabled={atCap}
        >
          <UserRoundPlus size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="wx-bg-row-action"
          onClick={(event) => {
            event.stopPropagation();
            onOpenDetail();
          }}
        >
          <Eye size={13} aria-hidden="true" />
          เปิดรายละเอียด
        </button>
      </td>
    </tr>
  );
}

function PersonCard({ candidate, activeCount, onOpenDetail, onQuickAdd }) {
  const pipeline = describeSourcePipelineStatus(candidate.sourceType, candidate.pipelineStatus);
  const countKnown = typeof activeCount === "number";
  const atCap = countKnown && activeCount >= MAX_ACTIVE_BUDDIES;

  return (
    <div className="wx-bg-card" role="button" tabIndex={0} onClick={onOpenDetail} data-label={candidate.name}>
      <div className="wx-bg-card-head">
        <span className="wx-bg-cell-strong">{candidate.name}</span>
        <span className={`wx-badge wx-badge--${pipeline.badgeVariant}`}>{pipeline.label}</span>
      </div>
      {candidate.phoneNumber && (
        <span className="wx-bg-cell-sub wx-bg-cell-sub--icon">
          <Phone size={10} aria-hidden="true" /> {candidate.phoneNumber}
        </span>
      )}
      <span className="wx-bg-cell-sub">{[candidate.nationality, candidate.skillName].filter(Boolean).join(" · ") || "-"}</span>
      <div className="wx-bg-card-foot">
        <span className="wx-bg-cell-strong">Buddy {countKnown ? `${activeCount} / ${MAX_ACTIVE_BUDDIES}` : "..."}</span>
        <div className="wx-bg-row-actions-cell">
          <button
            type="button"
            className="wx-bg-icon-button wx-bg-quick-add"
            title="เพิ่ม Buddy"
            aria-label={`เพิ่ม Buddy สำหรับ ${candidate.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onQuickAdd();
            }}
            disabled={atCap}
          >
            <UserRoundPlus size={14} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
